/**
 * Pinia-стор очереди модерации компаний-партнёров (DEV-795). По образцу
 * features/agents/stores/agents.store. Пагинация серверная; клиентская фильтрация
 * (search/minConfidence) применяется к загруженной странице.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { companyVerificationsApi } from '../api/companyVerifications.api'
import { confidencePercent } from '../mappers/companyVerification.mapper'
import { STATUS_ACTION_NEEDED, STATUS_OVERDUE } from '../utils/verificationEnums'
import type {
  CompanyVerification,
  VerificationDecision,
  VerificationFilter,
  VerificationStats,
} from '../types/companyVerification.types'

export const useCompanyVerificationsStore = defineStore('company-verifications', () => {
  const items = ref<CompanyVerification[]>([])
  const isLoadingList = ref(false)
  const error = ref<string | null>(null)

  const currentPage = ref(1)
  const pageSize = ref(20)
  const totalItems = ref(0)
  const totalPages = ref(0)

  // Счётчик «Требуют действия» (ai_candidates + manager_review) для бейджа-призыва в сайдбаре.
  const pendingCount = ref(0)

  // Просроченные по SLA (DEV-1165): красят бейдж сайдбара в алерт — модератор должен увидеть
  // проблему, не открывая раздел.
  const overdueCount = ref(0)

  const filters = ref<VerificationFilter>({})

  const stats = ref<VerificationStats>({
    actionNeeded: null,
    noResult: null,
    acceptedMonth: null,
    slaOverdue: null,
  })

  /** Клиентская доводка страницы: search по названию, порог уверенности. */
  function applyClientFilters(rows: CompanyVerification[]): CompanyVerification[] {
    const q = filters.value.search?.trim().toLowerCase()
    const min = filters.value.minConfidence ?? 0
    return rows.filter((r) => {
      if (min && confidencePercent(r.confidence) < min) return false
      if (q && !r.typedCompanyName.toLowerCase().includes(q)) return false
      return true
    })
  }

  async function loadItems(newFilters?: VerificationFilter, page?: number): Promise<void> {
    isLoadingList.value = true
    error.value = null
    try {
      if (newFilters !== undefined) filters.value = newFilters
      if (page !== undefined) currentPage.value = page

      const result = await companyVerificationsApi.list(
        filters.value,
        currentPage.value,
        pageSize.value,
      )

      items.value = applyClientFilters(result.items)
      totalItems.value = result.total
      totalPages.value = Math.max(1, Math.ceil(result.total / pageSize.value))
      currentPage.value = result.page || currentPage.value
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to load verifications'
      items.value = []
    } finally {
      isLoadingList.value = false
    }
  }

  /**
   * KPI-счётчики. Источника-агрегата на бэке пока нет (follow-up
   * GetCompanyVerificationsStats), поэтому «Требуют действия»/«Нет результата»/«Просрочено SLA»
   * берём из pagination.total лёгкими запросами perPage=1; «Принято за месяц» остаётся null
   * (в UI — тире), пока бэк не отдаст агрегат за период.
   */
  async function loadStats(): Promise<void> {
    try {
      const [actionNeeded, noResult, overdue] = await Promise.all([
        companyVerificationsApi.list({ verificationStatus: STATUS_ACTION_NEEDED }, 1, 1),
        companyVerificationsApi.list({ verificationStatus: 'ai_no_result' }, 1, 1),
        companyVerificationsApi.list({ verificationStatus: STATUS_OVERDUE }, 1, 1),
      ])
      stats.value = {
        actionNeeded: actionNeeded.total,
        noResult: noResult.total,
        acceptedMonth: null,
        slaOverdue: overdue.total,
      }
      overdueCount.value = overdue.total
    } catch {
      // KPI — вспомогательные; ошибку глушим, очередь остаётся рабочей.
    }
  }

  /**
   * Лёгкие счётчики очереди для бейджа в сайдбаре (perPage=1, только total): сколько ждёт
   * действия и сколько из этого уже просрочено. Отдельно от loadStats, чтобы сайдбар мог
   * опрашивать их дёшево, не завися от KPI-экрана.
   */
  async function fetchPendingCount(): Promise<void> {
    try {
      const [pending, overdue] = await Promise.all([
        companyVerificationsApi.list({ verificationStatus: STATUS_ACTION_NEEDED }, 1, 1),
        companyVerificationsApi.list({ verificationStatus: STATUS_OVERDUE }, 1, 1),
      ])
      pendingCount.value = pending.total
      overdueCount.value = overdue.total
    } catch {
      // Бейдж вспомогательный — ошибку глушим, не мешаем остальному UI.
    }
  }

  async function confirm(
    id: string,
    decision: VerificationDecision,
    selectedCompanyId?: string,
    overrideReason?: string,
  ): Promise<CompanyVerification> {
    const updated = await companyVerificationsApi.confirmByManager(
      id,
      decision,
      selectedCompanyId,
      overrideReason,
    )
    await Promise.all([loadItems(undefined, currentPage.value), loadStats(), fetchPendingCount()])
    return updated
  }

  function resetFilters(): void {
    filters.value = {}
    currentPage.value = 1
  }

  return {
    items,
    isLoadingList,
    error,
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    filters,
    stats,
    pendingCount,
    overdueCount,
    loadItems,
    loadStats,
    fetchPendingCount,
    confirm,
    resetFilters,
  }
})
